import React, { memo } from 'react';
import { SUPPORTED_COUNTRIES } from '@honeybook/i18n';
import type { SupportedCountry } from '@honeybook/i18n';
import HBInputField from '../HBInputField/HBInputField';
import HBDropdownField, {
    HBDropdownFieldProps
} from '../HBDropdownField/HBDropdownField';
import { SearchableDropdownWrapper } from './HBAddress.styles';

import { getAddressFieldConfig } from './HBAddress.utils';
import { useHBTranslation } from '../HBUIProvider/HBUIProvider';

export type HBAddressStateFieldProps = Omit<
    HBDropdownFieldProps,
    'label' | 'size' | 'options' | 'onChange' | 'value' | 'placeholder'
> & {
    /** Placeholder text for the input or dropdown. */
    placeholder?: string;
    /** HTML id for the field; forwarded to the underlying input or dropdown. */
    id: string;
    /** Identifier segment appended to the component name to build data-testid. */
    testIdName?: string;
    /** Explicit test ID string; sets data-testid directly. */
    testId?: string;
    /**
     * Field size. Limited to the sizes supported by both HBInputField and HBDropdownField.
     * Defaults to 'sm'.
     */
    size?: 'sm' | 'md' | 'lg' | 'xl';
    /** The currently selected country code. Determines label, required status, and whether to render a dropdown or free-text input. */
    country: string;
    /** Current field value. */
    value: string;
    /** Called with the new string value on every change — normalized across both the dropdown and free-text input rendering modes. */
    onChange: (value: string) => void;
    /** Called on blur. Useful for triggering validation or marking the field as touched. Not forwarded to the dropdown rendering mode. */
    onBlur?: () => void;
    /** Override the auto-derived label (e.g. State, Province, County). */
    label?: string;
    /**
     * Override the auto-derived required status.
     * When omitted, derived from config.regionRequired: true for US/CA/AU, false for GB and non-supported countries.
     */
    required?: boolean;
};

const HBAddressStateField = ({
    country,
    value,
    onChange,
    onBlur,
    label,
    required,
    size,
    ...rest
}: HBAddressStateFieldProps) => {
    const t = useHBTranslation();
    const isSupportedCountry = (SUPPORTED_COUNTRIES as string[]).includes(
        country
    );
    const config = isSupportedCountry
        ? getAddressFieldConfig(country as SupportedCountry)
        : null;

    const derivedLabel =
        label ?? t(config ? config.regionKey : 'hbAddress.region.region');
    const derivedRequired = required ?? config?.regionRequired ?? false;

    if (config && config.regionOptions.length > 0) {
        return (
            <SearchableDropdownWrapper>
                <HBDropdownField
                    placeholder={t(config.regionSelectKey)}
                    {...rest}
                    size={size}
                    label={derivedLabel}
                    options={config.regionOptions}
                    required={derivedRequired}
                    value={value}
                    onChange={onChange}
                    isSearchable
                />
            </SearchableDropdownWrapper>
        );
    }

    return (
        <HBInputField
            {...rest}
            size={size}
            label={derivedLabel}
            required={derivedRequired}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange(e.target.value)
            }
            onBlur={onBlur && (() => onBlur())}
        />
    );
};

export default memo(HBAddressStateField);
